import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Form, Button, Table,
  Modal, Badge, Alert, Spinner, Image,
  DropdownButton, Dropdown, InputGroup, Tab, Nav, Tabs
} from 'react-bootstrap';
import axios from 'axios';
import { FaPhotoVideo, FaImage, FaVideo, FaFilter, FaSearch, FaEdit, FaTrash, FaEye, FaEyeSlash } from 'react-icons/fa';
import AdminNavbar from './components/AdminNavbar';
import { BASE_URL, UPLOAD_BASE_URL } from '../../services/api';

// Gallery Card Component
const GalleryCard = ({ item, onEdit, onDelete, onToggleActive }) => {
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    return `${UPLOAD_BASE_URL}${imagePath}`;
  };

  const getYoutubeThumbnail = (url) => {
    if (!url) return null;
    const videoId = url.includes('youtube.com/embed/') 
      ? url.split('youtube.com/embed/')[1]
      : url.includes('youtu.be/') 
        ? url.split('youtu.be/')[1]
        : null;
    
    if (videoId) {
      return `https://img.youtube.com/vi/${videoId}/0.jpg`;
    }
    return null;
  };

  // Define formatDate function inside the component
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Card className="h-100 border-0 shadow-sm">
      <div className="position-relative">
        {item.gallery_type === 'photo' ? (
          <Card.Img
            variant="top"
            src={getImageUrl(item.image_path)}
            alt={item.title}
            style={{ height: '200px', objectFit: 'cover' }}
          />
        ) : (
          <div 
            className="bg-dark"
            style={{ 
              height: '200px', 
              background: `url(${getYoutubeThumbnail(item.video_url)}) center/cover no-repeat`
            }}
          >
            <div className="h-100 d-flex align-items-center justify-content-center">
              <FaVideo size={40} className="text-white" style={{ opacity: 0.8 }} />
            </div>
          </div>
        )}
        <div className="position-absolute top-0 end-0 p-2">
          <Badge bg={item.category_info?.color?.split(' ')[0] || 'secondary'}>
            <i className={`${item.category_info?.icon || 'fas fa-ellipsis-h'} me-1`}></i>
          </Badge>
        </div>
      </div>
      <Card.Body>
        <Card.Title className="h6 mb-2">{item.title}</Card.Title>
        <Card.Text className="small text-muted mb-3">
          {item.description && item.description.length > 100 
            ? item.description.substring(0, 100) + '...'
            : item.description || 'No description'}
        </Card.Text>
        <div className="d-flex justify-content-between align-items-center">
          <small className="text-muted">
            {formatDate(item.created_at)}
          </small>
          <div className="d-flex gap-2">
            <Button
              size="sm"
              variant={item.is_active ? "outline-success" : "outline-warning"}
              onClick={() => onToggleActive(item)}
              title={item.is_active ? 'Active' : 'Inactive'}
            >
              {item.is_active ? <FaEye /> : <FaEyeSlash />}
            </Button>
            <Button
              size="sm"
              variant="outline-primary"
              onClick={() => onEdit(item)}
              title="Edit"
            >
              <FaEdit />
            </Button>
            <Button
              size="sm"
              variant="outline-danger"
              onClick={() => onDelete(item.id)}
              title="Delete"
            >
              <FaTrash />
            </Button>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

// Media Grid Component
const MediaGrid = ({ items, onEdit, onDelete, onToggleActive }) => (
  <Row className="g-4">
    {items.map((item) => (
      <Col key={item.id} lg={4} md={6} sm={12}>
        <GalleryCard 
          item={item} 
          onEdit={onEdit}
          onDelete={onDelete}
          onToggleActive={onToggleActive}
        />
      </Col>
    ))}
  </Row>
);

const GalleryManager = () => {
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [user, setUser] = useState(null);
  
  // Form states
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Filter states
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Options
  const [categories, setCategories] = useState({});
  const [activeTab, setActiveTab] = useState('all'); // 'photos', 'videos', 'all'
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    gallery_type: 'photo',
    category: 'nature',
    image_path: '',
    video_url: '',
    is_active: true
  });

  const API_BASE_URL = BASE_URL;
  
  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || 'null');
    setUser(userData);
    
    fetchGallery();
    fetchOptions();
  }, [selectedCategory, selectedType]);

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      gallery_type: 'photo',
      category: 'nature',
      image_path: '',
      video_url: '',
      is_active: true
    });
    setImagePreview(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    window.location.href = '/admin/login';
  };

  const fetchGallery = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      
      let url = `${API_BASE_URL}/gallery.php?admin=true`;
      
      if (selectedCategory !== 'all') {
        url += `&category=${selectedCategory}`;
      }
      
      if (selectedType !== 'all') {
        url += `&type=${selectedType}`;
      }
      
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        const processedItems = response.data.data.map(item => ({
          ...item,
          id: parseInt(item.id),
          is_active: item.is_active === "1" || item.is_active === 1
        }));
        
        setGalleryItems(processedItems);
      } else {
        setError('Failed to load gallery');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchOptions = async () => {
    try {
      setOptionsLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_BASE_URL}/gallery.php?categories=true`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        setCategories(response.data.data.categories || {});
      }
    } catch (err) {
      console.error('Error fetching options:', err);
    } finally {
      setOptionsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTextareaChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleGalleryTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      gallery_type: type,
      image_path: type === 'photo' ? prev.image_path : '',
      video_url: type === 'video' ? prev.video_url : ''
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      // Set file for upload
      setFormData(prev => ({
        ...prev,
        image_file: file
      }));
    }
  };

  const removeImage = () => {
    setImagePreview(null);
    setFormData(prev => ({
      ...prev,
      image_path: '',
      image_file: null
    }));
  };

  const uploadImage = async (file) => {
    try {
      setUploadingImage(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('image', file);
      
      const response = await axios.post(`${API_BASE_URL}/upload_gallery_image.php`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        return response.data.image_url;
      }
      return null;
    } catch (err) {
      console.error('Image upload error:', err);
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleVideoUrlChange = (url) => {
    let processedUrl = url;
    
    // Extract YouTube video ID
    if (url.includes('youtube.com/watch?v=')) {
      const videoId = url.split('v=')[1];
      const ampersandPosition = videoId.indexOf('&');
      if (ampersandPosition !== -1) {
        processedUrl = videoId.substring(0, ampersandPosition);
      } else {
        processedUrl = videoId;
      }
      processedUrl = `https://www.youtube.com/embed/${processedUrl}`;
    }
    
    // Extract Vimeo video ID
    else if (url.includes('vimeo.com/')) {
      const videoId = url.split('vimeo.com/')[1];
      processedUrl = `https://player.vimeo.com/video/${videoId}`;
    }
    
    setFormData(prev => ({
      ...prev,
      video_url: processedUrl
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let dataToSend = { ...formData };
      
      // Remove image_file from dataToSend if it exists
      if (dataToSend.image_file) {
        delete dataToSend.image_file;
      }
      
      // If there's a new image to upload
      if (formData.image_file) {
        const uploadedUrl = await uploadImage(formData.image_file);
        if (uploadedUrl) {
          dataToSend.image_path = uploadedUrl;
        } else {
          setError('Failed to upload image');
          return;
        }
      }
      
      const token = localStorage.getItem('token');
      let response;
      
      if (editingItem) {
        response = await axios.put(
          `${API_BASE_URL}/gallery.php?id=${editingItem.id}`,
          dataToSend,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      } else {
        response = await axios.post(`${API_BASE_URL}/gallery.php`, dataToSend, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
      
      if (response.data.success) {
        setSuccess(editingItem ? 'Gallery item updated!' : 'Gallery item added!');
        setShowModal(false);
        resetForm();
        setEditingItem(null);
        fetchGallery();
        
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Operation failed: ' + (response.data.message || ''));
      }
    } catch (err) {
      setError('Error saving gallery item: ' + (err.response?.data?.message || err.message));
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this gallery item? This will also delete the associated image.')) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.delete(`${API_BASE_URL}/gallery.php?id=${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.data.success) {
          setSuccess('Gallery item deleted!');
          fetchGallery();
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setError('Failed to delete gallery item: ' + (response.data.message || ''));
        }
      } catch (err) {
        setError('Error deleting gallery item');
        console.error(err);
      }
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title,
      description: item.description || '',
      gallery_type: item.gallery_type,
      category: item.category,
      image_path: item.image_path || '',
      video_url: item.video_url || '',
      is_active: item.is_active === true || item.is_active === "1" || item.is_active === 1
    });
    
    // Set image preview if image_path exists
    if (item.image_path) {
      setImagePreview(getImageUrl(item.image_path));
    }
    
    setShowModal(true);
  };

  const handleToggleActive = async (item) => {
    try {
      const currentActive = item.is_active === true || item.is_active === "1" || item.is_active === 1;
      const newActiveStatus = !currentActive;
      
      const updatedData = {
        title: item.title,
        description: item.description || '',
        gallery_type: item.gallery_type,
        category: item.category,
        image_path: item.image_path || '',
        video_url: item.video_url || '',
        is_active: newActiveStatus ? 1 : 0
      };
      
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_BASE_URL}/gallery.php?id=${item.id}`,
        updatedData,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.data.success) {
        fetchGallery();
      } else {
        setError('Failed to update status: ' + (response.data.message || ''));
      }
    } catch (err) {
      console.error('Error toggling active status:', err);
      setError('Failed to update status');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
    resetForm();
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    return `${UPLOAD_BASE_URL}${imagePath}`;
  };

  const getThumbnailUrl = (imagePath) => {
    if (!imagePath) return null;
    const pathParts = imagePath.split('/');
    const fileName = pathParts[pathParts.length - 1];
    return `${UPLOAD_BASE_URL}/uploads/gallery/thumbs/${fileName}`;
  };

  const filteredItems = galleryItems.filter(item => {
    const matchesSearch = searchTerm === '' || 
                         item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesTab = activeTab === 'all' || 
                      (activeTab === 'photos' && item.gallery_type === 'photo') ||
                      (activeTab === 'videos' && item.gallery_type === 'video');
    
    return matchesSearch && matchesTab;
  });

  if (loading) {
    return (
      <>
        <AdminNavbar onLogout={handleLogout} user={user} />
        <Container className="mt-5 text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading gallery...</p>
        </Container>
      </>
    );
  }

  return (
    <>
      <AdminNavbar onLogout={handleLogout} user={user} />
      <Container fluid className="py-4">
        <Row className="mb-4">
          <Col>
            <h2>Gallery Manager</h2>
            <p className="text-muted">Manage your photo and video gallery</p>
          </Col>
          <Col className="text-end">
            <Button variant="primary" onClick={() => setShowModal(true)}>
              <FaPhotoVideo className="me-2" />Add Gallery Item
            </Button>
          </Col>
        </Row>

        {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
        {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

        {/* Filters */}
        <Card className="mb-4">
          <Card.Body>
            <Row>
              <Col md={3}>
                <Form.Group>
                  <Form.Label><FaFilter className="me-2" />Category</Form.Label>
                  <Form.Select 
                    value={selectedCategory} 
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  >
                    <option value="all">All Categories</option>
                    {Object.entries(categories).map(([key, category]) => (
                      <option key={key} value={key}>
                        <i className={`${category.icon} me-2`}></i>
                        {category.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Type</Form.Label>
                  <Form.Select 
                    value={selectedType} 
                    onChange={(e) => setSelectedType(e.target.value)}
                  >
                    <option value="all">All Types</option>
                    <option value="photo">Photos Only</option>
                    <option value="video">Videos Only</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group>
                  <Form.Label><FaSearch className="me-2" />Search</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      placeholder="Search by title or description..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <Button 
                      variant="outline-secondary" 
                      onClick={() => setSearchTerm('')}
                      disabled={!searchTerm}
                    >
                      Clear
                    </Button>
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Tabs */}
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className="mb-4"
        >
          <Tab eventKey="all" title="All Media">
            <MediaGrid 
              items={filteredItems} 
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggleActive={handleToggleActive}
            />
          </Tab>
          <Tab eventKey="photos" title="Photos">
            <MediaGrid 
              items={filteredItems.filter(item => item.gallery_type === 'photo')} 
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggleActive={handleToggleActive}
            />
          </Tab>
          <Tab eventKey="videos" title="Videos">
            <MediaGrid 
              items={filteredItems.filter(item => item.gallery_type === 'video')} 
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggleActive={handleToggleActive}
            />
          </Tab>
        </Tabs>

        {/* No Gallery Items */}
        {filteredItems.length === 0 && (
          <div className="text-center py-5 text-muted">
            <FaPhotoVideo size={60} className="mb-3" />
            <p>No gallery items found. Add your first gallery item!</p>
          </div>
        )}

        {/* Add/Edit Modal */}
        <Modal show={showModal} onHide={handleCloseModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              {editingItem ? 'Edit Gallery Item' : 'Add Gallery Item'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>Title *</Form.Label>
                    <Form.Control
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., Beautiful Sunset"
                    />
                  </Form.Group>
                </Col>
                
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Category *</Form.Label>
                    <Form.Select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      required
                    >
                      {Object.entries(categories).map(([key, category]) => (
                        <option key={key} value={key}>
                          <i className={`${category.icon} me-2`}></i>
                          {category.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  name="description"
                  value={formData.description}
                  onChange={handleTextareaChange}
                  rows={3}
                  placeholder="Describe this gallery item..."
                />
              </Form.Group>
              
              <Row className="mb-4">
                <Col>
                  <Form.Label>Media Type *</Form.Label>
                  <div className="d-flex gap-3">
                    <Button
                      type="button"
                      variant={formData.gallery_type === 'photo' ? 'primary' : 'outline-primary'}
                      onClick={() => handleGalleryTypeChange('photo')}
                    >
                      <FaImage className="me-2" /> Photo
                    </Button>
                    <Button
                      type="button"
                      variant={formData.gallery_type === 'video' ? 'primary' : 'outline-primary'}
                      onClick={() => handleGalleryTypeChange('video')}
                    >
                      <FaVideo className="me-2" /> Video
                    </Button>
                  </div>
                </Col>
              </Row>
              
              {formData.gallery_type === 'photo' ? (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>Upload Image *</Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      disabled={uploadingImage}
                    />
                    <Form.Text className="text-muted">
                      Recommended: JPG, PNG, WebP (max 5MB)
                    </Form.Text>
                  </Form.Group>
                  
                  {/* Image Preview */}
                  {(imagePreview || formData.image_path) && (
                    <div className="mb-3">
                      <Form.Label>Image Preview</Form.Label>
                      <div className="d-flex align-items-center">
                        <Image 
                          src={imagePreview?.startsWith('data:') ? imagePreview : getImageUrl(formData.image_path)}
                          alt="Preview" 
                          thumbnail 
                          style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'cover' }}
                          className="me-3"
                        />
                        
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={removeImage}
                        >
                          <FaTrash className="me-1" /> Remove Image
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <Form.Group className="mb-3">
                  <Form.Label>Video URL *</Form.Label>
                  <Form.Control
                    type="url"
                    name="video_url"
                    value={formData.video_url}
                    onChange={(e) => handleVideoUrlChange(e.target.value)}
                    placeholder="https://www.youtube.com/watch?v=... or https://vimeo.com/..."
                    required
                  />
                  <Form.Text className="text-muted">
                    Supports YouTube and Vimeo URLs
                  </Form.Text>
                  
                  {formData.video_url && (
                    <div className="mt-3">
                      <Form.Label>Video Preview</Form.Label>
                      <div className="ratio ratio-16x9">
                        <iframe
                          src={formData.video_url}
                          title="Video preview"
                          allowFullScreen
                          className="rounded"
                        ></iframe>
                      </div>
                    </div>
                  )}
                </Form.Group>
              )}
              
              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  name="is_active"
                  label="Active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                />
                <Form.Text className="text-muted">
                  Inactive items won't be shown on the website
                </Form.Text>
              </Form.Group>
              
              {/* Preview Section */}
              <Card className="border-secondary mt-3">
                <Card.Header>
                  <h6 className="mb-0">Preview</h6>
                </Card.Header>
                <Card.Body>
                  <div className="d-flex align-items-start">
                    <div className="me-3">
                      {formData.gallery_type === 'photo' ? (
                        <div 
                          className="rounded"
                          style={{
                            width: '100px',
                            height: '100px',
                            background: `url(${imagePreview || getImageUrl(formData.image_path) || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2Y4ZjhmOCIvPjx0ZXh0IHg9IjUwIiB5PSI1MCIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjEyIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjYzBjMGMwIiBkeT0iLjNlbSI+SW1hZ2U8L3RleHQ+PC9zdmc+'}) center/cover`
                          }}
                        ></div>
                      ) : (
                        <div className="bg-light rounded d-flex align-items-center justify-content-center"
                          style={{ width: '100px', height: '100px' }}>
                          <FaVideo size={30} className="text-muted" />
                        </div>
                      )}
                    </div>
                    <div className="flex-grow-1">
                      <h5 className="mb-1">{formData.title || 'Gallery Item Title'}</h5>
                      <div className="mb-2">
                        <Badge bg={categories[formData.category]?.color?.split(' ')[0] || 'secondary'}>
                          <i className={`${categories[formData.category]?.icon || 'fas fa-ellipsis-h'} me-1`}></i>
                          {categories[formData.category]?.name || 'Category'}
                        </Badge>
                        <Badge bg={formData.gallery_type === 'photo' ? 'info' : 'danger'} className="ms-2">
                          {formData.gallery_type === 'photo' ? 'Photo' : 'Video'}
                        </Badge>
                      </div>
                      {formData.description && (
                        <p className="text-muted small mb-0">
                          {formData.description.length > 100 
                            ? formData.description.substring(0, 100) + '...'
                            : formData.description}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="mt-3 small text-muted">
                    <div className="d-flex">
                      <div className="me-4">
                        <i className="fas fa-circle me-1"></i>
                        Status: 
                        <span className={`ms-2 ${formData.is_active ? 'text-success' : 'text-warning'}`}>
                          {formData.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={uploadingImage}>
                {uploadingImage && <Spinner animation="border" size="sm" className="me-2" />}
                {editingItem ? 'Update' : 'Add'} Gallery Item
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </>
  );
};

export default GalleryManager;